import { SectionThemeProvider } from "@trustpilot/constellation-components";
import { HtmlText, interpolate, Text, useTranslations } from "ilenia";
import { useHistory } from "react-router-dom";

import { trackingConst } from "../../../../constants";
import { useAnalytics } from "../../../../utils/analytics";
import { ScreenReaderOnlyDiv, ScreenReaderOnlyH2 } from "../../../styled";
import {
  StyledActiveTrialOuter,
  StyledTrial,
  StyledTrialInfo,
  StyledTrialName,
  StyledTrialTextContainer,
  StyledActiveTrialHeadingContainer,
  StyledInvertedTypography,
  StyledCancelLink,
  LinkContainer,
  StyledProgressContainer,
  StyledDaysLeftContainer,
  StyledCurrentProgress,
  StyledRemainingProgress,
  VerticallyCenteredTypography,
  StyledDarkTypography,
  FlexibleHeightButton,
} from "../../styled";
import * as Types from "../../types/types";
import { getTrialDaysRemaining } from "../../utils/getTrialDaysRemaining";

const TrialInfo: React.FC<Types.TrialInfo> = ({ trial, freeTrialDashboardEnabled, isOnCancelledProductLedTrial }) => {
  const cancelTrialLink = "/subscription/cancel-trial";
  const trialDashboardLink = "/subscription/trial-dashboard";
  const newLocalisationRoot = "Sidebar.newTrial";
  const legacyLocalisationRoot = "Sidebar.trial";
  const badgeTextLocalisationKey = `${newLocalisationRoot}.${isOnCancelledProductLedTrial ? "cancelled" : "freeTrial"}`;
  const analytics = useAnalytics();
  const history = useHistory();
  const [translations] = useTranslations();

  const daysLeft = getTrialDaysRemaining(trial?.subscriptionStartDate, trial?.duration, new Date());

  if (daysLeft <= 0) {
    return null;
  }
  const trialProgressPercentage = (daysLeft / trial?.duration) * 100;
  const trialRemainingPercentage = 100 - trialProgressPercentage;

  const screenReaderHeading = `${interpolate(translations[`${newLocalisationRoot}.plan`], {
    plan: trial?.planName,
  })} ${translations[badgeTextLocalisationKey]}`;
  const cancelTrialText = `${translations[`${newLocalisationRoot}.cancelFreeTrial`]}`;
  const goToDashboardText = `${translations[`${newLocalisationRoot}.goToDashboard`]}`;

  // show plan name and trial info over two lines for languages where the text is too long
  const MAX_TEXT_LENGTH_BEFORE_COLLAPSE = 20;
  const collapseTrialInfo = screenReaderHeading.length > MAX_TEXT_LENGTH_BEFORE_COLLAPSE;

  const handleLinkClick = (e: React.MouseEvent, link: string, trackingName: string) => {
    const currentLocationPath = history.location?.pathname;
    e.preventDefault();

    analytics.track(trackingConst.events.linkClicked, {
      context: trackingConst.contexts.sidebar,
      name: trackingName,
    });

    history.push(link);

    // If the user is already in the business upgrades microsite, we need to reload the page to show the correct content
    if (currentLocationPath?.includes("/subscription") && currentLocationPath !== link) {
      location.reload();
    }
  };

  return trial?.isLegacy ? (
    <StyledTrial>
      <>
        <StyledTrialName>
          <StyledInvertedTypography as="span" variant="body-m">
            <Text id={`${legacyLocalisationRoot}.yourTrial`} />
            {trial?.planName !== "" ? <b>{trial.planName}</b> : <Text id={`${legacyLocalisationRoot}.addOnModules`} />}
          </StyledInvertedTypography>
        </StyledTrialName>
        <StyledTrialInfo>
          <StyledInvertedTypography as="span" variant="body-m">
            {daysLeft === 1 ? (
              <Text id={`${legacyLocalisationRoot}.oneDayLeft`} />
            ) : (
              <Text id={`${legacyLocalisationRoot}.daysLeft`} interpolations={{ numberOfDays: daysLeft }} />
            )}
          </StyledInvertedTypography>
        </StyledTrialInfo>
      </>
    </StyledTrial>
  ) : (
    <StyledActiveTrialOuter aria-labelledby="plan-free-trial-info">
      <ScreenReaderOnlyH2 id="plan-free-trial-info">{screenReaderHeading}</ScreenReaderOnlyH2>
      <StyledActiveTrialHeadingContainer aria-hidden collapseTrialInfo={collapseTrialInfo}>
        <StyledInvertedTypography variant="heading-xs" id="start-trial-heading">
          <Text id={`${newLocalisationRoot}.plan`} interpolations={{ plan: trial?.planName }} />
        </StyledInvertedTypography>
        <StyledTrialTextContainer cancelled={isOnCancelledProductLedTrial} collapseTrialInfo={collapseTrialInfo}>
          <VerticallyCenteredTypography variant="heading-xxxs" id="start-trial-heading" appearance="inherit">
            <Text id={badgeTextLocalisationKey} />
          </VerticallyCenteredTypography>
        </StyledTrialTextContainer>
      </StyledActiveTrialHeadingContainer>
      <ScreenReaderOnlyDiv>{`${interpolate(translations[`${newLocalisationRoot}.timeLeftAria`], {
        numberOfDays: daysLeft,
      })}`}</ScreenReaderOnlyDiv>
      <div aria-hidden>
        <StyledInvertedTypography variant="body-s" id="start-trial-heading">
          <Text id={`${newLocalisationRoot}.timeLeft`} />
        </StyledInvertedTypography>
        <StyledProgressContainer>
          <StyledCurrentProgress width={trialRemainingPercentage} />
          <StyledRemainingProgress width={trialProgressPercentage} />
        </StyledProgressContainer>
        <StyledDaysLeftContainer>
          <StyledInvertedTypography variant="body-s" id="start-trial-heading">
            <HtmlText
              id={`${newLocalisationRoot}.${daysLeft === 1 ? `oneDayRemaining` : `daysRemaining`}`}
              interpolations={{ numberOfDays: daysLeft, boldStart: "<b>", boldEnd: "</b>" }}
            />
          </StyledInvertedTypography>
        </StyledDaysLeftContainer>
      </div>
      {freeTrialDashboardEnabled ? (
        <LinkContainer>
          <SectionThemeProvider theme="dark-green">
            <FlexibleHeightButton
              as="a"
              appearance="primary"
              wide
              size="s"
              onClick={(e: React.MouseEvent) => {
                handleLinkClick(e, trialDashboardLink, trackingConst.names.freeTrialDashboard);
              }}
              href={trialDashboardLink}
            >
              <StyledDarkTypography>{goToDashboardText}</StyledDarkTypography>
            </FlexibleHeightButton>
          </SectionThemeProvider>
        </LinkContainer>
      ) : (
        trial?.trialState !== "TrialCancelled" && (
          <LinkContainer>
            <StyledCancelLink
              onClick={(e: React.MouseEvent) => {
                handleLinkClick(e, cancelTrialLink, trackingConst.names.cancelFreeTrial);
              }}
              href={cancelTrialLink}
            >
              <StyledInvertedTypography
                variant={cancelTrialText.length > MAX_TEXT_LENGTH_BEFORE_COLLAPSE ? "body-s" : "body-m"}
              >
                {cancelTrialText}
              </StyledInvertedTypography>
            </StyledCancelLink>
          </LinkContainer>
        )
      )}
    </StyledActiveTrialOuter>
  );
};
export default TrialInfo;
